// What the Request access modal does once a submit comes back, and once the
// drafts it made have been signed in the basket's panel (or not). Pure, so
// each outcome is unit tested; the modal only carries the decisions out.
//
// A request whose policy wants its requester's passkey is created as a draft.
// The modal stays open to sign it in place: a clean submit closes once the
// signing stops, as any submit would; a partial failure goes back to the
// basket, as it does today.

import type { UseToastOptions } from "@chakra-ui/react";
import type { DraftSigningResult } from "pages/Members/StepUp/draftSigning";

export type SubmitResult = {
  total: number;
  failed: number;
  errors: string[];
  /** Ids of the requests created as drafts awaiting the requester's signature. */
  drafts: string[];
};

export type SubmitFollowUp = {
  toast: UseToastOptions | null;
  /** Close the modal now: nothing is left to do in it. */
  close: boolean;
  /** Drafts to sign in the panel, and whether the modal closes after. */
  sign: { draftIds: string[]; closeAfter: boolean } | null;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export const submitFollowUp = ({
  total,
  failed,
  errors,
  drafts,
}: SubmitResult): SubmitFollowUp => {
  const sign = (closeAfter: boolean) =>
    drafts.length > 0 ? { draftIds: drafts, closeAfter } : null;

  // Drafts are not submitted until they are signed.
  const submitted = total - failed - drafts.length;

  if (failed === 0) {
    return {
      toast:
        submitted > 0
          ? {
              status: "success",
              variant: "subtle",
              title: submitted === 1 ? "Request submitted" : `${submitted} requests submitted`,
            }
          : null,
      close: drafts.length === 0,
      sign: sign(true),
    };
  }

  if (failed === total) {
    // All failed: surface the specific reasons (duplicate, no auth, ...).
    return {
      toast: {
        status: "error",
        variant: "subtle",
        title: total === 1 ? "Request failed" : "Requests failed",
        description: errors.join(" "),
      },
      close: false,
      sign: null,
    };
  }

  return {
    toast: {
      status: "warning",
      variant: "subtle",
      title: `${submitted} of ${total} requests submitted`,
      description:
        `${failed} could not be submitted` +
        (errors.length ? `. ${errors.join(" ")}` : ".") +
        (drafts.length
          ? ` ${drafts.length} ${plural(drafts.length, "needs", "need")} your signature.`
          : ""),
    },
    close: false,
    sign: sign(false),
  };
};

/** Once the panel stops: say how it went, and close if the submit was clean. */
export const afterSigning = (
  { signed, unsigned, handoff }: DraftSigningResult,
  closeAfter: boolean,
): { toast: UseToastOptions | null; close: boolean } => {
  // The signing page takes over, and says how it went itself.
  if (handoff) return { toast: null, close: true };

  if (unsigned === 0) {
    return {
      toast: {
        status: "success",
        variant: "subtle",
        title:
          signed === 1
            ? "Request signed and submitted"
            : `${signed} requests signed and submitted`,
      },
      close: closeAfter,
    };
  }

  const waiting =
    signed === 0
      ? plural(
          unsigned,
          "It waits in My Access, where you can sign it to send it for approval.",
          "They wait in My Access, where you can sign each one to send it for approval.",
        )
      : `${signed} signed and submitted. ` +
        plural(
          unsigned,
          "The other waits in My Access, where you can sign it to send it for approval.",
          "The others wait in My Access, where you can sign each one to send it for approval.",
        );
  return {
    toast: {
      status: "info",
      variant: "subtle",
      title:
        unsigned === 1 ? "Request not sent yet" : `${unsigned} requests not sent yet`,
      description: waiting,
    },
    close: closeAfter,
  };
};
